class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.details = this.closest('[data-header-search]');
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.abortController = null;
    this.cachedResults = new Map();
    this.searchingLabel = this.dataset.searchingLabel || 'Searching...';
    this.unavailableLabel = this.dataset.unavailableLabel || 'Search is temporarily unavailable.';
    this.onInputChange = this.debounce(this.onInputChange.bind(this), 250);
  }

  connectedCallback() {
    if (!this.input || !this.results) return;

    this.input.addEventListener('input', this.onInputChange);
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim()) this.onInputChange();
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.close();
      this.details?.removeAttribute('open');
      this.details?.querySelector('summary')?.focus();
    });
  }

  onInputChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.renderStatus(this.searchingLabel);
    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    const cacheKey = searchTerm.toLowerCase();
    const cached = this.cachedResults.get(cacheKey);

    if (cached) {
      this.renderProducts(cached, searchTerm);
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    const searchUrl = new URL(
      `${window.Shopify?.routes?.root || '/'}search/suggest.json`,
      window.location.origin
    );

    searchUrl.searchParams.set('q', searchTerm);
    searchUrl.searchParams.set('resources[type]', 'product');
    searchUrl.searchParams.set('resources[limit]', '6');
    searchUrl.searchParams.set('resources[limit_scope]', 'all');
    searchUrl.searchParams.set('resources[options][unavailable_products]', 'last');

    fetch(searchUrl.toString(), {
      signal: this.abortController.signal,
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Predictive search request failed with ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const products = data?.resources?.results?.products || [];
        this.cachedResults.set(cacheKey, products);

        if (this.input.value.trim() !== searchTerm) return;
        this.renderProducts(products, searchTerm);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
        this.renderStatus(this.unavailableLabel);
      });
  }

  renderProducts(products, searchTerm) {
    if (this.input.value.trim() !== searchTerm) return;

    const rows = products.map((product) => {
      const imageUrl =
        product?.featured_image?.url ||
        product?.featured_image ||
        product?.image ||
        '';

      const title = this.escapeHtml(product?.title || '');
      const url = this.escapeAttribute(product?.url || '#');
      const image = imageUrl
        ? `<img class="jl-predictive__image" src="${this.escapeAttribute(imageUrl)}" alt="" loading="lazy">`
        : '<span class="jl-predictive__placeholder"></span>';

      return `
        <li class="jl-predictive__item">
          <a href="${url}" class="jl-predictive__product">
            <span class="jl-predictive__image-wrap" aria-hidden="true">${image}</span>
            <span class="jl-predictive__meta">
              <span class="jl-predictive__title">${title}</span>
              <span class="jl-predictive__price">${this.formatPrice(product?.price)}</span>
            </span>
          </a>
        </li>
      `;
    }).join('');

    const allUrl =
      `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last`;

    this.results.innerHTML = products.length
      ? `
        <div class="jl-predictive">
          <section class="jl-predictive__group">
            <h3 class="jl-predictive__heading">Products</h3>
            <ul class="jl-predictive__list" role="list">${rows}</ul>
          </section>
          <a class="jl-predictive__search-all" href="${allUrl}">
            Search "${this.escapeHtml(searchTerm)}"
          </a>
        </div>
      `
      : `
        <div class="predictive-search-results predictive-search-results--status">
          <p>No results found for "${this.escapeHtml(searchTerm)}".</p>
        </div>
      `;

    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  formatPrice(price) {
    const currency = window.Shopify?.currency?.active || 'AUD';
    const locale = document.documentElement.lang || 'en-AU';

    const raw = String(price ?? '').trim();
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return this.escapeHtml(raw);

    const amount = raw.includes('.') ? numeric : numeric / 100;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format(amount);
    } catch (_) {
      return `A${amount.toFixed(2)}`;
    }
  }

  renderStatus(message) {
    this.results.hidden = false;
    this.results.innerHTML = `
      <div class="predictive-search-results predictive-search-results--status">
        <p>${this.escapeHtml(message)}</p>
      </div>
    `;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.input?.setAttribute('aria-expanded', 'false');
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }

  debounce(fn, wait) {
    let timeoutId;
    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
